import { Box, Checkbox, Chip, FormHelperText, ListItemText, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import React, { useRef } from "react";
import { useEffect, useState } from "react";
import PlaceholderControl from "../PlaceholderControl";
import styles from "./SelectMultiple.module.scss";

interface Props<T> {
  id: string;
  currentValues: T[];
  source: T[];
  readOnly: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: string | false | string[] | undefined;
  fixedValues?: T[] | undefined;
  onChange: (newValues: T[]) => void;
  onBlur?: (e: any) => void;
  keySelector: (obj: T) => string;
  labelSelector: (obj: T) => string;
}

function _selectMultiple<T extends any>({
  id,
  currentValues,
  source,
  readOnly,
  placeholder,
  disabled,
  error,
  fixedValues,
  onChange,
  onBlur,
  keySelector,
  labelSelector,
}: Props<T>) {
  const fixedOptions = fixedValues ? [...fixedValues.map((x) => keySelector(x).toUpperCase())] : [];

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const reference = useRef();

  useEffect(() => {
    const initialSelectedValues = currentValues.map((x) => keySelector(x).toUpperCase());
    setSelectedValues([...initialSelectedValues]);
  }, [currentValues, source, keySelector]);

  useEffect(() => {
    if (fixedValues && fixedValues.length > 0) {
      const fixedNotSelected = fixedValues.filter(
        (x) => !currentValues.some((c) => keySelector(c).toUpperCase() === keySelector(x).toUpperCase())
      );
      onChange([...currentValues, ...fixedNotSelected]);
    }
  }, [fixedValues]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    let selectedIds = typeof value === "string" ? value.split(",") : value;
    selectedIds = [...fixedOptions, ...selectedIds.filter((x) => !fixedOptions.includes(x))];

    setSelectedValues(selectedIds);

    const newValue = source.filter((x) => selectedIds.includes(keySelector(x).toUpperCase()));
    onChange([...newValue]);
  };

  const findLabel = (key: string) => {
    const obj = source.find((x) => keySelector(x).toUpperCase() === key.toUpperCase());
    return obj ? labelSelector(obj) : "";
  };

  const handleBlur = (event: any) => {
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <>
      <Select
        ref={reference}
        className={`${styles.selectMultiple} ${error ? "Mui-error" : ""} `}
        labelId={`${id}-label`}
        id={id}
        key={`select_multiple_${id}`}
        name={id}
        multiple
        fullWidth
        size="small"
        disabled={disabled}
        value={selectedValues}
        onChange={handleChange}
        onBlur={handleBlur}
        inputProps={{ readOnly: readOnly }}
        displayEmpty
        renderValue={(selected) => {
          return selected && selected.length > 0 ? (
            <Box>
              {selected.map((value) => {
                const isInOption = source.findIndex((x) => keySelector(x).toUpperCase() === value.toUpperCase()) > -1;
                return isInOption ? (
                  <Chip key={`chip_${id}_${value}`} label={findLabel(value)} />
                ) : (
                  <span key={`chip_${id}_${value}`}></span>
                );
              })}
            </Box>
          ) : (
            <PlaceholderControl text={placeholder || ""} />
          );
        }}
      >
        {[...source].map((x) => {
          const key = keySelector(x).toUpperCase();
          const label = labelSelector(x);
          return (
            <MenuItem key={`key_${id}_${key}`} value={key}>
              <Checkbox checked={selectedValues.includes(key)} disabled={fixedOptions.includes(key)} />
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText className="Mui-error">{error}</FormHelperText>
    </>
  );
}

const SelectMultiple = React.memo(
  _selectMultiple,
  (prevProps, nextProps) =>
    prevProps.id === nextProps.id &&
    prevProps.currentValues === nextProps.currentValues &&
    prevProps.source === nextProps.source &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.error === nextProps.error &&
    prevProps.fixedValues === nextProps.fixedValues &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.onChange === nextProps.onChange
) as typeof _selectMultiple;

export default SelectMultiple;
